import React from "react";
import style from "./Monetization.module.css";

// Shown on the Watch Fraud review page, behind "How does this work?".
//
// WRITTEN FOR A NON-TECHNICAL READER. No rule names, no column names, no
// jargon. If you edit this, keep it in the words an ordinary person would
// use — the people reading it are deciding whether to accuse someone of
// cheating, and they do not work on this system.
//
// Every answer describes what the code really does today, not what it is
// meant to do. If behaviour changes, change the answer in the same commit.
// The "marking it checked does not take money back" answer matters most:
// people naturally assume the opposite.
const FAQ = [
  {
    q: "Where does creator money come from?",
    a: (
      <>
        Each month a fixed pot of money is set aside from subscription income. Creators share that
        pot based on how many minutes people watched their videos.
        <br />
        <br />
        The pot does <strong>not</strong> grow when someone watches more. So if one person fakes
        watch time, they do not create new money — they take a bigger slice, and every honest
        creator gets a bit less. That is why we check for cheating at all.
      </>
    ),
  },
  {
    q: "If I mark something here as checked, does that take their money back?",
    a: (
      <>
        <strong>No.</strong> "Looks fine — mark as checked" only saves a note that you looked at it.
        Nobody's payment changes.
        <br />
        <br />
        The button that <strong>does</strong> change money is the other one:{" "}
        <strong>"This was cheating — stop paying for it"</strong>. You have to type what you found
        first, and confirm a second time, because it means a real person will not be paid for that
        viewing. You can undo it afterwards.
      </>
    ),
  },
  {
    q: "So when does watch time actually stop being paid for?",
    a: (
      <>
        Two different ways:
        <ol className={style.faqList}>
          <li>
            <strong>You decide.</strong> Open something on this list, look at the evidence, and
            press "This was cheating — stop paying for it". Only the viewing in that one finding
            stops counting — never the person's whole history.
          </li>
          <li>
            <strong>Automatically</strong>, but only for one thing: an account claiming more hours
            in a day than a day actually has. That only happens if someone switched on "Stop paying
            for impossible watch time" in Settings, which is off unless you turn it on.
          </li>
        </ol>
        Nothing else removes watch time on its own. Everything else waits for a person to decide.
      </>
    ),
  },
  {
    q: "I pressed it by mistake. Can I undo it?",
    a: (
      <>
        Yes. Open the same finding again and press <strong>"Undo this — count the watch time
        again"</strong>. The watch time goes back to counting immediately.
        <br />
        <br />
        One timing catch worth knowing: if the month has already closed and payouts have been sent,
        undoing it afterwards does not pull money back from anyone. So it is worth going through
        this list <strong>before</strong> the month ends.
      </>
    ),
  },
  {
    q: "Does being on this list mean the person definitely cheated?",
    a: (
      <>
        No — and this matters before you contact anyone. Real people sometimes look like this by
        accident:
        <ul className={style.faqList}>
          <li>A big family or a shared flat can look like several accounts in one place.</li>
          <li>
            Many phone networks put thousands of unrelated customers on one connection, so strangers
            can look like they live together.
          </li>
          <li>Some people use privacy apps that hide their location for everything they do.</li>
          <li>Someone skimming through a long video will skip around a lot.</li>
        </ul>
        Treat this list as "worth a look", not as proof.
      </>
    ),
  },
];

export default function MonetizationFaq() {
  return (
    <div className={style.advancedBody}>
      {FAQ.map((item, i) => (
        <details className={style.faqItem} key={i}>
          <summary className={style.faqQ}>{item.q}</summary>
          <div className={style.faqA}>{item.a}</div>
        </details>
      ))}
    </div>
  );
}
